import { useCpk } from '@swarm/core/contracts/cpk'
import { compareTokensBy } from '@swarm/core/shared/utils/filters/compare-tokens-by'
import { unifyAddress, unifyAddressToId, useAccount } from '@swarm/core/web3'
import { NormalizedPoorSecurity } from '@swarm/types/normalized-entities'
import InfiniteTable from '@swarm/ui/presentational/InfiniteTable'
import AlertPanel from '@swarm/ui/swarm/AlertPanel'
import AssetFilter, { getAssetFilter } from '@swarm/ui/swarm/AssetFilter'
import map from 'lodash/map'
import uniqBy from 'lodash/uniqBy'
import { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { NavLink } from 'react-router-dom'
import { Box, Card, Heading, Link, Text } from 'rimble-ui'
import styled from 'styled-components/macro'
import { useQueryParam } from 'use-query-params'

import useSecuritiesRewards from 'src/hooks/staking/useSecuritiesRewards'
import useStakes from 'src/hooks/staking/useStakes'
import { ROUTES } from 'src/routes'

import { AssetParam, PageLimit } from '../consts'

import MyStakesRow from './MyStakesRow'

const StyledLink = styled(Link)`
  font-size: 16px;
  font-weight: 700;
`

const StyledInfiniteTable = styled(InfiniteTable)`
  col {
    width: 10%;
  }

  colgroup.asset {
    col {
      &:last-child {
        min-width: 220px;
      }
    }
  }

  th,
  td {
    text-align: center;
    min-width: 90px;
    white-space: nowrap;

    &:first-child {
      width: 120px;
    }

    &:last-child {
      width: 150px;
    }

    &.asset {
      text-align: left;
    }
  }

  td {
    color: ${(props) => props.theme.colors['near-black']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints?.[2]}) {
    col {
      width: 0%;
    }

    th,
    td {
      &:nth-child(1),
      &:nth-child(4),
      &:nth-child(5),
      &:nth-child(6) {
        width: 0px;
        display: none;
      }

      &:nth-child(2) {
        width: 100px;
      }

      &:nth-child(3) {
        width: 90px;
      }

      &:nth-child(4) {
        text-align: center;
        width: 70px;
      }

      &:nth-child(5) {
        width: 150px;
      }

      &:nth-child(6) {
        width: 70px;
      }

      &:nth-child(7) {
        width: 140px;
      }

      &:nth-child(8) {
        width: 100px;
      }

      &:last-child {
        width: 200px;
      }
    }
  }
`

const MyStakesTab = () => {
  const { t } = useTranslation('staking')
  const account = useAccount()
  const cpk = useCpk()

  const [assetsParam, setAssetsParam] = useQueryParam('assets', AssetParam)

  const filter = useMemo(() => {
    if (!account || !cpk?.address) return {}
    const initFilter = {
      unstaked: false,
      ...(assetsParam.length > 0 && {
        // eslint-disable-next-line camelcase
        stakedSecurity_in: assetsParam,
      }),
    }

    return {
      or: [
        {
          maker: unifyAddressToId(cpk?.address),
          ...initFilter,
        },
        {
          maker: unifyAddressToId(account),
          ...initFilter,
        },
      ],
    }
  }, [account, assetsParam, cpk?.address])

  const { stakes, loading, fetchMore, refetching } = useStakes({
    skip: !cpk?.address || !account,
    variables: { filter },
  })

  const { securitiesRewards, loading: rewardsLoading } = useSecuritiesRewards()

  const stakeTokens = useMemo(
    () =>
      uniqBy(
        stakes.map((stake) => stake.stakedSecurity, [stakes]),
        'id',
      ),
    [stakes],
  )

  const [hasMore, setHasMore] = useState(true)

  const loadMore = useCallback(async () => {
    if (stakes && hasMore && !loading && cpk?.address) {
      const { data } = await fetchMore(stakes.length, PageLimit)
      setHasMore(data?.swarmStakes?.length === PageLimit)
    }
  }, [stakes, hasMore, loading, cpk?.address, fetchMore])

  const sortedStakes = useMemo(() => {
    return stakes.sort((a, b) =>
      compareTokensBy<NormalizedPoorSecurity>(['volume24hs', 'desc'])(
        a.stakedSecurity,
        b.stakedSecurity,
      ),
    )
  }, [stakes])

  return (
    <Box
      width="100%"
      bg="background"
      display="flex"
      flexDirection="column"
      p={[3, 3, 4]}
      flexGrow={1}
    >
      <AlertPanel page="staking" promptPassport={false} />
      <Card
        width="100%"
        p={4}
        borderRadius={1}
        overflow="hidden"
        display="flex"
        flexDirection="column"
        justifyContent="stretch"
      >
        <Heading
          as="h3"
          mt={0}
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
        >
          <Text.span
            color="grey"
            fontSize={3}
            lineHeight="28px"
            fontWeight={5}
            flexGrow={1}
            flexShrink={0}
            mb={3}
          >
            {t('headings.myStakes')}
          </Text.span>
          <AssetFilter
            value={getAssetFilter(stakeTokens, assetsParam)}
            onSubmit={(value) => setAssetsParam(map(value, 'id'))}
            flexGrow={0}
            flexShrink={1}
            flexBasis="auto"
            tokens={stakeTokens}
            loading={loading || rewardsLoading}
          />
        </Heading>
        <StyledInfiniteTable
          colgroup={
            <>
              <colgroup className="asset">
                <col />
                <col />
              </colgroup>
              <colgroup>
                <col />
                <col />
                <col />
                <col />
                <col />
              </colgroup>
              <colgroup>
                <col />
              </colgroup>
            </>
          }
          head={
            <tr>
              <th>{t(`th.tx`)}</th>
              <th className="asset" scope="colgroup">
                {t(`th.stakeAgainst`)}
              </th>
              <th>{t(`th.marketCap`)}</th>
              <th>{t(`th.vol24`)}</th>
              <th>{t(`th.totalStaked`)}</th>
              <th>{t(`th.apy`)}</th>
              <th>{t(`th.myStakes`)}</th>
              <th>{t(`th.status`)}</th>
              <th> </th>
            </tr>
          }
          loading={loading}
          hasMore={hasMore}
          loadMore={loadMore}
          noResults={
            <tr>
              <td colSpan={7}>
                <Text.p
                  color="grey"
                  textAlign="center"
                  width="100%"
                  fontWeight={5}
                >
                  <Trans
                    t={t}
                    i18nKey="noStakes"
                    components={[
                      <NavLink
                        component={StyledLink}
                        key={ROUTES.STAKING}
                        to={generatePath(ROUTES.STAKING)}
                      />,
                    ]}
                  />
                </Text.p>
              </td>
            </tr>
          }
        >
          {refetching
            ? []
            : sortedStakes.map((stake) => (
                <MyStakesRow
                  key={`${stake.id} ${stake.stakedAt}`}
                  stake={stake}
                  smtReward={
                    securitiesRewards[unifyAddress(stake.stakedSecurity.id)]
                  }
                />
              ))}
        </StyledInfiniteTable>
      </Card>
    </Box>
  )
}

export default MyStakesTab
